import { AbstractControl, FormGroup, FormControl } from '@angular/forms';
import { isEmpty, omit, filter, uniqBy, sortBy } from 'lodash';
import { TareaCondicion } from '@app/shared';
import { escapeRegexp } from 'ngx-bootstrap/typeahead';
import { isNumber } from 'util';


export interface Condicion {
  condicion: string;
  alias: string;
  opciones: Opcion[];
}

export interface Opcion {
  id_opcion: number;
  opcion: string;
  mascara: string;
  orden: number;
  seleccionado: boolean;
}

/**
 * Utilidades desacopladas que se pueden utilizar staticamente.
 *
 * @export
 * @class Static
 */
export class Static {
  /**
   * Setea un conjunto de propiedades de un objeto
   * utilizando los datos de otro.
   * Útil cuando se quiere setear propiedades de una clase
   * de forma perezosa con datos parciales de otro objeto.
   *
   * @param {*} target - objeto que se va a setear.
   * @param {*} values - valores utilizados para setear.
   * @memberof Static
   */
  static setAll(target: {}, values: {}) {
    for (const key in values) {
      if (values.hasOwnProperty(key)) {
        target[key] = values[key];
      }
    }
  }

  /**
   * Elimina las propiedades extra de un objeto.
   * Se toma como base las propiedades del primer
   * objeto(source), las propiedades que este posea
   * son las que perduran en el segundo(target).
   *
   * @param {Object} source
   * @param {Object} target
   * @memberof Static
   */
  static hardCast(source: Object | string[], target: Object): any {
    const _target = Object.assign({}, target);
    const validKeys = typeof source === 'object' ? Object.keys(source) : source;
    for (const key in target) {
      if (target.hasOwnProperty(key)) {
        if (!validKeys.includes(key)) {
          delete _target[key];
        }
      }
    }
    return _target;
  }

  /**
   * Determina si un elemento nativo del DOM posee
   * una clase en especifico.
   *
   * @static
   * @param {*} el
   * @param {string} name
   * @returns
   * @memberof Static
   */
  static nativeElementHasClass(el: any, name: string) {
    return new RegExp('(?:^|\\s+)' + name + '(?:\\s+|$)').test(el.className);
  }

  /**
   * Agrega una clase a un elemento nativo del DOM.
   *
   * @static
   * @param {*} el
   * @param {string} name
   * @memberof Static
   */
  static nativeElemenAddClass(el: any, name: string) {
    if (!this.nativeElementHasClass(el, name)) {
      el.className = el.className ? [el.className, name].join(' ') : name;
    }
  }

  /**
   * Elimina una clase a un elemento nativo del DOM.
   *
   * @static
   * @param {*} el
   * @param {string} name
   * @memberof Static
   */
  static nativeElemenRemoveClass(el: any, name: string) {
    if (this.nativeElementHasClass(el, name)) {
      el.className = el.className.replace(new RegExp('(?:^|\\s+)' + name + '(?:\\s+|$)', 'g'), '');
    }
  }

  /**
   * Retorna el nombre de un control dentro de un formulario reactivo.
   *
   * @static
   * @param {AbstractControl} control
   * @returns {string}
   * @memberof Static
   */
  static getControlName(control: AbstractControl): string {
    const parent = control['_parent'];
    let controlName = null;
    // FormGroup, tiene un diccionario con los nombres de los controles
    if (parent instanceof FormGroup) {
      Object.keys(parent.controls).forEach(name => {
        if (control === parent.controls[name]) {
          controlName = name;
        }
      });
    }
    return controlName;
  }

  /**
   * Mapea todas las condiciones con sus opciones
   * generando una estructura más facil de iterar.
   *
   * @example
   * [
   *  {
   *    "condicion": "finalizar tarea",
   *    "opciones": [
   *      {
   *        "id_opcion": 524,
   *        "opcion": "si",
   *        "mascara": "",
   *        "seleccionado": true
   *      },
   *      {
   *        "id_opcion": 525,
   *        "opcion": "no",
   *        "mascara": ""
   *        "seleccionado": false
   *      }
   *    ]
   *  }
   * ]
   *
   *
   * @param {TareaCondicion[]} arr
   * @returns {Condicion[]}
   * @memberof Static
   */
  static getMapConditions(arr: TareaCondicion[]): Condicion[] {
    const conditions: Condicion[] = [];
    let descriptions: Array<{
      condicion: string;
      alias: string;
    }> = [];
    // Almacenar todas las condiciones
    arr.forEach(element => {
      if (element.tarea_condicion) {
        descriptions.push({
          condicion: element.tarea_condicion,
          alias: element.alias
        });
      } else {
        conditions.push({
          condicion: element['condicion'],
          alias: element['alias'],
          opciones: element['opciones']
        });
      }

    });
    // Eliminar descripciones duplicadas
    descriptions = uniqBy(descriptions, 'condicion');
    descriptions.forEach(description => {
      const condition: Condicion = {
        condicion: description.condicion,
        alias: description.alias,
        opciones: []
      };
      // Filtrar opciones de la condición
      const _filter = filter(arr, { tarea_condicion: description.condicion });
      _filter.forEach(element => {
        const option: Opcion = {
          id_opcion: element.id_opcion,
          opcion: element.opcion,
          orden: element.orden,
          mascara: element.mascara,
          seleccionado: element.seleccion
        };
        condition.opciones.push(option);
      });
      // Ordenar opciones en base a su 'id_opcion'
      condition.opciones = sortBy(condition.opciones, 'id_opcion');
      conditions.push(condition);
    });
    for (let index = 0; index < conditions.length; index++) {
      conditions[index].opciones.sort((a, b) => a.orden - b.orden);
    }
    return conditions;
  }

  /**
   * Retorna la copia de un elemento de cualquier tipo.
   * Es util cuando se desea realiar una copia de un objeto o un array
   * pero no se desea tener una referencia de este objeto.
   *
   * @static
   * @param {*} element
   * @returns {*}
   * @memberof Static
   */
  static copyObjet<T>(element: T, notCopy: Array<String> = []): T {
    let copyElement: any;
    if (typeof element !== 'object') {
      copyElement = element;
    } else {
      if (Array.isArray(element)) {
        copyElement = [];
        element.forEach(subElement => {
          if (subElement) {
            copyElement.push(this.copyObjet(subElement, notCopy));
          }
        });
      } else {
        copyElement = {};
        Object.keys(element).forEach(el => {
          const skipKey = notCopy.indexOf(el);
          if (skipKey === -1) {
            if (typeof element[el] !== 'object' || element[el] === null || element[el] instanceof Date) {
              copyElement[el] = element[el];
            } else {
              if (element[el]) {
                copyElement[el] = this.copyObjet(element[el], notCopy);
              }
            }
          }
        });
      }
    }
    return copyElement;
  }

  /**
   * Ajuste decimal de un número.
   *
   * @param {string} type El tipo de ajuste.
   * @param {*} value El número.
   * @param {number} exp El exponente (El logaritmo de ajuste en base 10).
   * @returns
   * @memberof Static
   */
  static decimalAdjust(type: string, value: any, exp: number) {
    // Si exp es undefined o cero...
    if (typeof exp === 'undefined' || +exp === 0) {
      return Math[type](value);
    }
    value = +value;
    exp = +exp;
    // Si el valor no es un número o exp no es un entero...
    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
      return NaN;
    }
    // Shift
    value = value.toString().split('e');
    value = Math[type](+(value[0] + 'e' + (value[1] ? (+value[1] - exp) : -exp)));
    // Shift back
    value = value.toString().split('e');
    return +(value[0] + 'e' + (value[1] ? (+value[1] + exp) : exp));
  }

  /**
   * Ejecuta un for de forma sincronica
   *
   * @static
   * @template T
   * @param {Array<T>} array
   * @param {(value: T, index: number, item: Array<T>) => void} callback
   * @memberof Static
   */
  static async asyncForEach<T>(array: Array<T>, callback: (value: T, index: number, item: Array<T>) => void) {
    for (let index = 0; index < array.length; index++) {
      await callback(array[index], index, array);
      continue;
    }
  }

  /**
   * Da formato de numero logico a un numero con separador de miles
   *
   * @static
   * @param {string} val
   * @param {string} [thousandsSeparator]
   * @param {string} [decimalSeparator]
   * @returns {string}
   * @memberof Static
   */
  static numberFormatWithoutThousandsSeparator(
    val: string,
    thousandsSeparator?: string,
    decimalSeparator?: string
  ): string {
    let number = val;
    let reTS: RegExp;
    let reDS: RegExp;
    if (thousandsSeparator) {
      reTS = new RegExp(escapeRegexp(thousandsSeparator), 'g');
    }
    if (decimalSeparator) {
      reDS = new RegExp(escapeRegexp(decimalSeparator), 'g');
    }
    if (thousandsSeparator && decimalSeparator) {
      number = String(val).replace(reTS, '').replace(reDS, '.');
    } else if (thousandsSeparator) {
      number = String(val).replace(reTS, '');
    } else if (decimalSeparator) {
      number = String(val).replace(reDS, '.');
    }
    return number;
  }

  /**
   * Aplica el separador de miles respetando el separador de decimales
   *
   * @static
   * @param {string} value
   * @param {string} [separatorThousands=',']
   * @param {('.' | ',')} [separatorDecimals='.']
   * @returns {string}
   * @memberof Static
   */
  static applyThousandsSeparatorFormat(
    value: any,
    separatorThousands: string = ',',
    separatorDecimals: '.' | ',' = '.'
  ): string {
    const thousands = require('thousands');
    const re = new RegExp(escapeRegexp(separatorThousands), 'g');
    let valueSplit: string[] = [];
    if (separatorDecimals !== separatorThousands) {
      if (typeof value === 'number') {
        valueSplit = String(value).split('.');
      } else {
        valueSplit = value.split(separatorDecimals);
      }
      let v = valueSplit[0];
      valueSplit.splice(0, 1);
      v = thousands(v.replace(re, ''), separatorThousands);
      if (valueSplit.length) {
        valueSplit.forEach(val => {
          v += separatorDecimals + val.replace(re, '');
        });
      }
      return v;
    } else {
      return thousands(value.replace(re, ''), separatorThousands);
    }
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/shared/util/static.ts